import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';

import ChurnWinBackButton from './button';
import { CHURN_WIN_BACK_ACTIONS, useTrackChurnWinBackAction } from './event-tracking/use-track-churn-win-back-action';
import { CHURN_WIN_BACK_COOKIE_KEY, getCouponDuration } from './helpers';
import messages from './messages';
import styles from './styles.module.less';
import UserDateTimezoneFormat from '../../../../../components/user-date-timezone-format';
import { useOnComponentMount } from '../../../../../hooks/use-on-component-mount';
import { CookieService } from '../../../../../services/cookie-service';
import { PENDING_CANCELLATION, CANCELLED } from '../../constants/account-issues';

export const ChurnWinBackBanner = ({ billingCycleEndDateTimeUTC, cancelledDateTimeUTC, status }) => {
  const coupon = CookieService.getCookieValue(CHURN_WIN_BACK_COOKIE_KEY);
  const eventPayload = useMemo(() => ({ status, coupon }), [coupon, status]);
  const trackChurnWinBackAction = useTrackChurnWinBackAction(CHURN_WIN_BACK_ACTIONS.DISPLAYED_COUPON, eventPayload);

  useOnComponentMount(trackChurnWinBackAction);

  const monthCount = getCouponDuration(coupon);
  const billingCycleEndDate = <UserDateTimezoneFormat dateTime={billingCycleEndDateTimeUTC} />;
  const cancelledDate = <UserDateTimezoneFormat dateTime={cancelledDateTimeUTC} />;

  const banners = {
    [PENDING_CANCELLATION]: {
      title: <FormattedMessage {...messages.pendingCancellationTitle} values={{ monthCount }} />,
      message: <FormattedMessage {...messages.pendingCancellationMessage} values={{ endDate: billingCycleEndDate, monthCount }} />,
      buttonMessage: <FormattedMessage {...messages.pendingCancellationButton} />,
    },
    [CANCELLED]: {
      title: <FormattedMessage {...messages.cancelledTitle} values={{ endDate: cancelledDate }} />,
      message: <FormattedMessage {...messages.cancelledMessage} values={{ endDate: cancelledDate, monthCount }} />,
      buttonMessage: <FormattedMessage {...messages.cancelledButton} />,
    },
  };

  const { title, message, buttonMessage } = banners[status];

  return (
    <BannerMessageV2
      className={styles['churn-win-back-banner']}
      content={
        <>
          {title}
          <div className={styles['banner-message']}>{message}</div>
          <div className={styles['banner-button']}>
            <ChurnWinBackButton buttonMessage={buttonMessage} coupon={coupon} status={status} />
          </div>
        </>
      }
      status={BannerStatus.Warning}
    />
  );
};

ChurnWinBackBanner.propTypes = {
  billingCycleEndDateTimeUTC: PropTypes.string,
  cancelledDateTimeUTC: PropTypes.string,
  status: PropTypes.oneOf([PENDING_CANCELLATION, CANCELLED]).isRequired,
};
